/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, lazy, useEffect, useMemo, useState, Suspense } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment-timezone';

// Component imports
import { Button, Card, Col, Collapse, Dropdown, Form, Grid, Input, Menu, Row, Skeleton, Space, Spin, Table, Tooltip, Typography } from 'antd';
import { SearchOutlined, CaretRightOutlined, CloseOutlined, ReloadOutlined } from '@ant-design/icons';
import TopicModellerForm from '../Forms/TopicModellerForm';
import BotAvatar from '../UI/BotAvatar';
import ObservatoryFiltersRow, { useInitialTenantFilters, useObservatoryFilters, useObservatorySearchParams } from '../UI/ObservatoryFiltersRow';
import { ArrowRightIcon } from '../Icons/ArrowRightIcon';
import { ArrowDownIcon } from '../Icons/ArrowDownIcon';

// Misc imports
import { DEFAULT_DATETIME_FORMAT, DEFAULT_PAGINATION_SIZE } from '../../constants/main';
import { useFetchMissedQuestionsQuery, useLazyDownloadMissedQuestionsQuery, useLazyFetchMissedQuestionsQuery } from '../../store/missedQuestions/missedQuestionsApi';
import { IS_TOPIC_MODELLER_ENABLED } from '../../constants/env';
import { useFetchAutomatedAnswersQuery } from '../../store/automatedAnswers/automatedAnswersApi';
import { useFetchBotsQuery } from '../../store/bots/botsApi';
import { useFetchOrganizationQuery } from '../../store/organization/organizationApi';
import { revertBlobTypes } from '../../store/utils/revertBlobTypes';
import is from '../../utils/is';
import handleFileDownload from '../../utils/misc/handleFileDownload';
import userHasEditAccess from '../../utils/misc/hasEditAccess';

// Lazy Component Imports
const MissedQuestionDetailsPanel = lazy(() => import('../UI/MissedQuestionDetailsPanel')) ;

// Constant declarations
const { useBreakpoint } = Grid;
const COLLAPSE_TABS = {
  MISSED_QUESTIONS_TABLE_TAB: 'missedQuestionsTable',
  TOPIC_MODELLER_TAB: 'topicModeller'
};

const MissedQuestionsTable = (props) => {
  const { hideAgentsPicker } = props;
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { orgId } = useParams();
  const screens = useBreakpoint();
  const isMobileOrTabletSize = (screens.xs || screens.sm || screens.md || screens.lg) && !screens.xl;
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGINATION_SIZE);
  const [tenantId, setTenantId] = useState(null);

  let filterTimeout = 0;

  const { data: organization } = useFetchOrganizationQuery({ orgId }, { skip: !orgId });
  const { data: bots, isFetching: isFetchingBots } = useFetchBotsQuery({ orgId, urlParams: { includeBotDeployments: true } }, { skip: !orgId });

  const orgTimezone = organization?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [downloadMissedQuestions, { isLoading: isDownloadingMissedQuestions }] = useLazyDownloadMissedQuestionsQuery();
  const [fetchAllMissedQuestions, { isFetching: isFetchingAllMissedQuestions }] = useLazyFetchMissedQuestionsQuery();
  const { organizationRoles } = useSelector((state) => state.auth.currentUser);
  const isAdmin = userHasEditAccess(orgId, organizationRoles);

  const observatorySearchParams = useObservatorySearchParams(orgTimezone);
  const observatoryFilters = useObservatoryFilters(orgTimezone);
  const initialTenantFilters = useInitialTenantFilters();

  const dateParams = {
    startDate: observatorySearchParams.startDate,
    endDate: observatorySearchParams.endDate
  };

  const { data: missedQuestions, isFetching: isFetchingMissedQuestions, refetch: fetchMissedQuestions } = useFetchMissedQuestionsQuery({ orgId, urlParams: {
    ...observatorySearchParams,
    timeZone: encodeURIComponent(orgTimezone),
    page: Number(searchParams.get('page')) || 0,
    pageSize: pageSize,
    sortBy: searchParams.get('sortBy') || 'filteredOccurrences',
    order: searchParams.get('order') || 'desc',
    ...(searchParams.get('separator') && { separator: searchParams.get('separator') }),
    ...(searchParams.get('search') && { question: searchParams.get('search') }),
    ...(searchParams.get('type') && { type: searchParams.get('type') })
  } }, { skip: !observatorySearchParams.startDate || !observatorySearchParams.endDate || !organization });

  // eslint-disable-next-line no-unused-vars
  const { data: automatedAnswers } = useFetchAutomatedAnswersQuery({ orgId, tenantId }, { skip: !orgId || !tenantId });

  const [isMissedQuestionDetailsVisible, setIsMissedQuestionDetailsVisible] = useState(false);
  const [selectedMissedQuestionId, setSelectedMissedQuestionId] = useState(null);

  const sortingInfo = useMemo(() => {
    let order = searchParams.get('order') ? (searchParams.get('order') === 'desc' ? 'descend' : 'ascend') : 'descend';
    let columnKey = searchParams.get('sortBy') ? searchParams.get('sortBy') : 'filteredOccurrences';

    return { order, columnKey };
  }, [searchParams.get('order'), searchParams.get('sortBy')]);

  useEffect(() => {
    if(selectedRows?.length > 0 || selectedRowKeys.length > 0) {
      setSelectedRows([]);
      setSelectedRowKeys([]);
    }
  }, [missedQuestions?.total]);

  const onTopicModellerButtonClick = () => {
    const urlSearchParams = new URLSearchParams(location.search);
    urlSearchParams.set('topicModeller', 'true');

    navigate({
      pathname: location.pathname,
      search: urlSearchParams.toString()
    }, { replace: true });
  };

  const handleOnMissedQuestionOpen = (id) => {
    setSelectedMissedQuestionId(id);
    setIsMissedQuestionDetailsVisible(true);
  };

  const handleOnMissedQuestionClose = () => {
    setSelectedMissedQuestionId(null);
    setIsMissedQuestionDetailsVisible(false);
  };

  const columns = [
    {
      title: t('Agent'),
      dataIndex: 'tenantId',
      key: 'tenantId',
      width: '20%',
      render: tenantId => {
        const tenant = (bots?.items || []).find(bot => bot.id === tenantId);

        return isFetchingBots ? (
          <Skeleton.Button style={{ width: 100, height: 16 }} active size="small" />
        ) : tenant ? (
          <BotAvatar bot={tenant} avatarSize={16} />
        ) : (
          <Typography.Text type="secondary">{t('(agent not found)')}</Typography.Text>
        );
      }
    },
    {
      title: t('Question'),
      dataIndex: 'question',
      key: 'question',
      sorter: (a, b) => a.question.localeCompare(b.question),
      sortOrder: sortingInfo.columnKey === 'question' && sortingInfo.order,
      ellipsis: { showTitle: false },
      render: question => (
        <Tooltip overlayClassName="pointer-events-none" placement="topRight" title={question}>
          {question}
        </Tooltip>
      ),
      width: '60%'
    },
    {
      title: t('Occurrences'),
      dataIndex: 'filteredOccurrences',
      key: 'filteredOccurrences',
      width: '20%',
      align: 'center',
      sorter: {
        compare: (a, b) => a.filteredOccurrences - b.filteredOccurrences
      },
      sortOrder: sortingInfo.columnKey === 'filteredOccurrences' && sortingInfo.order
    },
    {
      title: t('Last Occurrence'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: '20%',
      sorter: {
        compare: (a, b) => a.updatedAt - b.updatedAt
      },
      sortOrder: sortingInfo.columnKey === 'updatedAt' && sortingInfo.order,
      render: updatedAt => {
        return (
          moment(updatedAt).tz(orgTimezone).format(DEFAULT_DATETIME_FORMAT)
        );
      }
    },
    {
      title: t('Actions'),
      key: 'actions',
      fixed: 'right',
      align: 'center',
      width: '15%',
      render: (_text, record) => {
        return (
          <div onClick={(e) => e.stopPropagation()} className={'d-flex align-items-center justify-content-center'}>
            <Tooltip overlayClassName="pointer-events-none" title={t('View Missed Question Details')}>
              <Button
                type="link"
                size="small"
                className={classNames('ml-2', {
                  'bg-primary text-white': record.id === selectedMissedQuestionId,
                  'bg-skeleton': record.id !== selectedMissedQuestionId
                })}
                icon={isMobileOrTabletSize ? <ArrowDownIcon /> : <ArrowRightIcon />}
                shape="circle"
                onClick={() => {
                  if (record.id === selectedMissedQuestionId) {
                    handleOnMissedQuestionClose();
                  } else {
                    handleOnMissedQuestionOpen(record.id);
                  }
                }}
              />
            </Tooltip>
          </div>
        );
      }
    }
  ].filter(item => !item.hidden);

  // On mount
  useEffect(() => {
    updateUrlSearchParams(Number(searchParams.get('page')) + 1, DEFAULT_PAGINATION_SIZE, { [sortingInfo.columnKey]: sortingInfo.order === 'descend' ? 'desc' : 'asc' });
  }, [orgTimezone]);

  const onDownload = async () => {
    const urlParams = {
      ...observatorySearchParams,
      timeZone: encodeURIComponent(orgTimezone),
      page: Number(searchParams.get('page')) || 0,
      pageSize: pageSize,
      sortBy: searchParams.get('sortBy') || 'filteredOccurrences',
      order: searchParams.get('order') || 'desc',
      ...(searchParams.get('separator') && { separator: searchParams.get('separator') }),
      ...(searchParams.get('search') && { search: searchParams.get('search') }),
      ...(searchParams.get('type') && { type: searchParams.get('type') })
    };

    const result = await downloadMissedQuestions({ orgId, urlParams }).unwrap();
    const file = revertBlobTypes(result);
    const fileName = file?.headers['content-disposition']?.split(new RegExp(['"'], 'g'))?.[1] ?? 'user_data.csv';
    handleFileDownload(file?.blob, fileName);
  };

  const updateUrlSearchParams = (current = 1, pageSize, sorting = { filteredOccurrences: 'desc' }) => {
    const urlSearchParams = new URLSearchParams(location.search);

    if (pageSize) {
      setPageSize(pageSize);
    }

    const searchFilter = form.getFieldValue('search');
    const separator = form.getFieldValue('separator');
    const type = form.getFieldValue('type');

    if (searchFilter) {
      urlSearchParams.set('search', encodeURIComponent(searchFilter));
    } else {
      urlSearchParams.delete('search');
    }

    if (separator) {
      urlSearchParams.set('separator', encodeURIComponent(separator));
    } else {
      urlSearchParams.delete('separator');
    }

    if (type) {
      urlSearchParams.set('type', encodeURIComponent(type));
    } else {
      urlSearchParams.delete('type');
    }

    urlSearchParams.set('page', current - 1);

    if (pageSize) {
      urlSearchParams.set('pageSize', pageSize);
    }

    if (sorting) {
      urlSearchParams.set('sortBy', Object.entries(sorting).flat()[0]);
      urlSearchParams.set('order', Object.entries(sorting).flat()[1]);
    } else {
      urlSearchParams.delete('sortBy');
      urlSearchParams.delete('order');
    }

    // Set the filters to the url
    navigate({
      pathname: location.pathname,
      search: urlSearchParams.toString()
    }, { replace: true });
  };

  const handleTableChange = (pagination, _filters, sorter) => {
    let sorting;
    if (sorter.column) {
      sorting = { [sorter.columnKey]: sorter.order === 'ascend' ? 'asc' : 'desc' };
    } else {
      if (sorter.field === 'filteredOccurrences' && searchParams.get('order') === 'desc') {
        sorting = { [sorter.columnKey]: 'asc' };
      }
    }
    updateUrlSearchParams(pagination.current, pagination.pageSize, sorting);
  };

  const onSelectAllChange = async (selected) => {
    if (selected) {
      if(missedQuestions?.total <= missedQuestions.pageSize) {
        setSelectedRowKeys(missedQuestions?.items.map(item => item.id));
        setSelectedRows(missedQuestions?.items);
        return;
      }

      const result = await fetchAllMissedQuestions({ orgId, urlParams: {
        ...observatorySearchParams,
        timeZone: encodeURIComponent(orgTimezone),
        pageSize: 9999, // Indicating to fetch all items
        sortBy: searchParams.get('sortBy') || 'filteredOccurrences',
        order: searchParams.get('order') || 'desc',
        ...(searchParams.get('separator') && { separator: searchParams.get('separator') }),
        ...(searchParams.get('search') && { question: searchParams.get('search') }),
        ...(searchParams.get('type') && { type: searchParams.get('type') })
      } }).unwrap();

      setSelectedRowKeys(result.items.map(item => item.id));
      setSelectedRows(result.items);
    } else {
      setSelectedRowKeys([]);
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (record, selected) => {
    if (selected) {
      setSelectedRowKeys([...selectedRowKeys, record.id]);
      setSelectedRows([...selectedRows, record]);
    } else {
      setSelectedRowKeys(selectedRowKeys.filter(key => key !== record.id));
      setSelectedRows(selectedRows.filter(row => row.id !== record.id));
    }
  };

  return (
    <Fragment>
      <Row gutter={[16, 16]} wrap={false}>
        <Col flex="auto">
          <Collapse
            ghost
            accordion
            activeKey={searchParams.get('topicModeller') ? COLLAPSE_TABS.TOPIC_MODELLER_TAB : COLLAPSE_TABS.MISSED_QUESTIONS_TABLE_TAB}
            destroyInactivePanel
            defaultActiveKey={COLLAPSE_TABS.MISSED_QUESTIONS_TABLE_TAB}
          >
            <Collapse.Panel
              key={COLLAPSE_TABS.MISSED_QUESTIONS_TABLE_TAB}
              showArrow={false}
              className="ant-collapse-p-0"
              forceRender
              header={''}
            >
              <Form
                layout={isMobileOrTabletSize ? 'vertical' : 'inline'}
                name="filtersForm"
                initialValues={{
                  tenantsFilter: initialTenantFilters,
                  rangePicker: [observatoryFilters.startDate, observatoryFilters.endDate],
                  search: searchParams.get('search') ? decodeURIComponent(searchParams.get('search')) : '',
                  separator: ','
                }}
                form={form}
              >
                <div className="w-100">
                  <ObservatoryFiltersRow
                    form={form}
                    rangePickerProps={{
                      disabled: isFetchingMissedQuestions || isDownloadingMissedQuestions,
                      timezone: orgTimezone
                    }}
                    agentsPickerProps={{
                      agentsList: bots?.items || [],
                      disabled: isFetchingMissedQuestions || isDownloadingMissedQuestions,
                      loading: isFetchingBots,
                      hidden: hideAgentsPicker
                    }}
                  />

                  <Row gutter={24}>
                    <Col flex="auto">
                      <Row gutter={[8, 8]} wrap={false}>
                        <Col flex="auto" className={isMobileOrTabletSize ? '' : 'max-field-width'}>
                          <Form.Item
                            name="search"
                            className="w-100 mb-2"
                          >
                            <Input
                              size="large"
                              placeholder={t('Search')}
                              allowClear
                              prefix={<SearchOutlined className="text-muted" />}
                              onChange={(e) => {
                                clearTimeout(filterTimeout);
                                filterTimeout = setTimeout(() => {
                                  form.setFieldsValue({ search: e.target.value });
                                  updateUrlSearchParams();
                                }, 700);
                              }}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Col>

                    <Col flex="none" className={isMobileOrTabletSize ? 'mt-2' : ''}>
                      <Row gutter={[8, 8]} justify={isMobileOrTabletSize ? 'start' : 'end'} wrap={false}>
                        {
                          isAdmin && IS_TOPIC_MODELLER_ENABLED && (
                            <Col flex="auto">
                              <Button
                                size="large"
                                disabled={isFetchingMissedQuestions || isFetchingAllMissedQuestions || selectedRows?.length === 0}
                                onClick={onTopicModellerButtonClick}
                              >
                                {t('Extract Topics')}
                              </Button>
                            </Col>
                          )
                        }

                        <Col flex="none">
                          <Dropdown.Button
                            size="large"
                            disabled={isDownloadingMissedQuestions}
                            overlay={
                              <Menu
                                onClick={(event) => {
                                  form.setFieldsValue({ separator: event.key });
                                  updateUrlSearchParams();
                                }}
                              >
                                <Menu.Item key=",">
                                  <Space align="middle">
                                    {
                                      form.getFieldValue('separator') === ',' ? (
                                        <CaretRightOutlined className="mr-1" />
                                      ) : (
                                        <span className="pl-5"></span>
                                      )
                                    }
                                    {t('Comma separator')} (,)
                                  </Space>
                                </Menu.Item>

                                <Menu.Item key=";">
                                  <Space align="middle">
                                    {
                                      form.getFieldValue('separator') === ';' ? (
                                        <CaretRightOutlined className="mr-1" />
                                      ) : (
                                        <span className="pl-5"></span>
                                      )
                                    }
                                    {t('Semicolon separator')} (;)
                                  </Space>
                                </Menu.Item>
                              </Menu>
                            }
                            onClick={onDownload}
                          >
                            {t('Download')}
                          </Dropdown.Button>

                          <Form.Item
                            name="separator"
                            hidden
                          >
                            <Input />
                          </Form.Item>
                        </Col>

                        <Col flex="none">
                          <Button
                            size="large"
                            type="primary"
                            disabled={isFetchingMissedQuestions}
                            icon={<ReloadOutlined />}
                            onClick={() => {
                              fetchMissedQuestions();
                            }}
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </div>
              </Form>

              <Row gutter={4} className="chat-session-wrapper flex-row">
                <Col
                  className="flex-basis-transition"
                  style={{
                    flexBasis: isMissedQuestionDetailsVisible && !isMobileOrTabletSize ? '60%' : '100%',
                    maxWidth: isMissedQuestionDetailsVisible && !isMobileOrTabletSize ? '60%' : '100%'
                  }}
                >
                  <Table
                    size="small"
                    rowKey="id"
                    scroll={{ y: 680 }}
                    columns={columns}
                    rowClassName={(record) => {
                      return record.id === selectedMissedQuestionId ? 'cursor-pointer bg-table-row-selected' : 'cursor-pointer';
                    }}
                    rowSelection={{
                      selectedRowKeys,
                      onSelectAll: onSelectAllChange,
                      hideSelectAll: Number(searchParams.get('page')) + 1 > 1,
                      onSelect: handleRowSelect,
                      preserveSelectedRowKeys: true
                    }}
                    onRow={(record) => ({
                      onClick: () => {
                        if (record.id === selectedMissedQuestionId) {
                          handleOnMissedQuestionClose();
                          setTenantId(null);
                        } else {
                          handleOnMissedQuestionOpen(record.id);
                          setTenantId(record.tenantId);
                        }
                      }
                    })}
                    onChange={handleTableChange}
                    showSorterTooltip={{ visible: false }}
                    pagination={{
                      size: 'default',
                      onChange: () => {
                        handleOnMissedQuestionClose();
                      },
                      total: missedQuestions?.total || 0,
                      defaultCurrent: 1,
                      current: Number(searchParams.get('page')) + 1,
                      pageSize: pageSize,
                      showSizeChanger: true,
                      showTotal: (total) => `${t('Total Missed Questions')}: ${total}`
                    }}
                    dataSource={missedQuestions && is.array(missedQuestions.items) && missedQuestions.items}
                    loading={isFetchingMissedQuestions || isFetchingAllMissedQuestions}
                    className="chat-session-table scroll-table"
                    expandable={{
                      expandIconColumnIndex: -1,
                      expandIcon: null,
                      onExpand: (expanded, record) => {
                        if (expanded) {
                          handleOnMissedQuestionOpen(record);
                        } else {
                          handleOnMissedQuestionClose();
                        }
                      },
                      expandedRowRender: record => {
                        return (
                          <Fragment>
                            {
                              selectedMissedQuestionId && (
                                <Suspense fallback={
                                  <Row className="w-100" justify="center" align="center">
                                    <Col xs={24} className="d-flex justify-content-center align-items-center">
                                      <Spin tip={t('Loading Missed Question details...')} />
                                    </Col>
                                  </Row>
                                }>
                                  <MissedQuestionDetailsPanel
                                    selectedMissedQuestion={record}
                                    missedQuestionId={selectedMissedQuestionId}
                                    requestParams={dateParams}
                                  />
                                </Suspense>
                              )
                            }
                          </Fragment>
                        );
                      },
                      rowExpandable: record => isMobileOrTabletSize,
                      expandedRowKeys: [selectedMissedQuestionId]
                    }}
                  />
                </Col>

                {
                  !isMobileOrTabletSize && (
                    <Col
                      className="flex-basis-transition"
                      style={{
                        flexBasis: isMissedQuestionDetailsVisible && !isMobileOrTabletSize ? '40%' : '0%',
                        maxWidth: isMissedQuestionDetailsVisible && !isMobileOrTabletSize ? '40%' : '0%'
                      }}
                    >
                      <Card
                        className="ml-2 opacity-transition border-0"
                        size="small"
                        title={
                          <Fragment>
                            <Row justify="space-between" align="middle">
                              <Col>
                                <Typography.Text strong> {t('Missed Question Details')} </Typography.Text>
                              </Col>
                              <Col>
                                <Tooltip title={t('Close')}>
                                  <Button
                                    type="text"
                                    shape="circle"
                                    onClick={() => {
                                      handleOnMissedQuestionClose();
                                    }}
                                    icon={<CloseOutlined />}
                                  />
                                </Tooltip>
                              </Col>
                            </Row>
                          </Fragment>
                        }
                        headStyle={{ backgroundColor: '#DDDFE9', height: '40px', borderRadius: '8px 8px 0px 0px' }}
                        style={{
                          opacity: isMissedQuestionDetailsVisible ? 1 : 0,
                          minHeight: '720px',
                          maxHeight: '720px'
                        }}
                      >
                        {
                          selectedMissedQuestionId && (
                            <Suspense
                              fallback={
                                <Row className="w-100" justify={'center'} align={'center'}>
                                  <Col xs={24} className="d-flex justify-content-center align-items-center">
                                    <Spin tip="Loading Missed Question details..." />
                                  </Col>
                                </Row>
                              }
                            >
                              <MissedQuestionDetailsPanel
                                selectedMissedQuestion={missedQuestions?.items?.find(question => question?.id === selectedMissedQuestionId)}
                                missedQuestionId={selectedMissedQuestionId}
                                requestParams={dateParams}
                              />
                            </Suspense>
                          )
                        }
                      </Card>
                    </Col>
                  )
                }
              </Row>
            </Collapse.Panel>

            <Collapse.Panel
              key={COLLAPSE_TABS.TOPIC_MODELLER_TAB}
              showArrow={false}
              className="ant-collapse-p-0"
              forceRender
              header={''}
            >
              <TopicModellerForm
                headerTitle="Topic Extraction"
                corpus={selectedRows?.map(item => item.question)}
                entityName={'Missed Questions'}
                completeData={selectedRows}
              />
            </Collapse.Panel>
          </Collapse>
        </Col>
      </Row>

    </Fragment>
  );
};

MissedQuestionsTable.propTypes = {
  hideAgentsPicker: PropTypes.bool
};

export default MissedQuestionsTable;
